import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, Check, X } from "lucide-react";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { Input } from "@/components/ui/input";
import api, { resolveMedia } from "@/lib/api";
import { FocalImage } from "@/components/FocalImage";
import { HOME } from "@/constants/testIds";
import { servicesList as staticServices } from "@/data/services";
import useSEO from "@/lib/useSEO";
import { useSettings } from "@/context/SettingsContext";
import { Reveal, MaskedLine, EASE } from "@/components/motion/Reveal";
import Marquee from "@/components/motion/Marquee";

const HERO_IMG = "https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4MzM0MDY4Mnww&ixlib=rb-4.1.0&q=85";

const MARQUEE_ITEMS = [
  "Interior Design", "Napoli · Pomigliano d'Arco", "Render Fotorealistici",
  "Direzione Artistica", "Arredi su Misura", "Consulenze Online",
];

const HERO_FOCAL = { alto: "center top", centro: "center center", basso: "center bottom", sinistra: "left center", destra: "right center" };

const eur0 = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const testimonials = [
  { text: "Ho avuto il piacere di affidarmi a Giovanna Matino per la progettazione degli interni della mia casa, e non potevo fare scelta migliore. Un gusto raffinato, una grande attenzione ai dettagli e una capacità straordinaria di trasformare gli spazi in ambienti eleganti e funzionali.", name: "Dario D." },
  { text: "Giovanna è l'architetto che ha realizzato la casa dei nostri sogni. Ha realizzato il progetto, ha seguito la parte burocratica e la parte operativa dei lavori dalla a alla z. Durante la fase di progettazione ha ascoltato tutte le nostre esigenze e in modo impeccabile ci ha accontentato in tutte le richieste. Gli imprevisti non sono mancati, come tutti i cantieri, ma in modo professionale è riuscita a saltare tutti gli ostacoli.", name: "Alessia A." },
  { text: "L'esperienza con l'architetto Giovanna Matino è stata super positiva, fin da subito con i suoi modi gentili e garbati ci ha conquistato. Competente e disponibile ci ha dimostrato grande professionalità dal progetto iniziale a tutta la fase di pratiche burocratiche. Farsi seguire da un architetto come lei è una garanzia.", name: "Anna C." },
];

function Chapter({ n, label, className = "" }) {
  return (
    <span className={`eyebrow inline-flex items-baseline gap-3 ${className}`}>
      <span className="font-serif italic text-base tracking-normal text-gold/70">{String(n).padStart(2, "0")}</span>
      <span className="hair-divider !w-8 self-center" />
      {label}
    </span>
  );
}

function BannerBlock({ b, index }) {
  const hasImg = !!(b.image_desktop || b.image_mobile);
  return (
    <Reveal delay={0.05 * index}>
      <div className="relative overflow-hidden bg-ink text-bone" data-testid={`home-banner-${b.id}`}>
        {hasImg && (
          <picture className="absolute inset-0">
            {b.image_mobile && <source media="(max-width: 767px)" srcSet={resolveMedia(b.image_mobile)} />}
            <img src={resolveMedia(b.image_desktop || b.image_mobile)} alt="" aria-hidden
              className="w-full h-full object-cover opacity-40" loading="lazy" />
          </picture>
        )}
        <div className="relative container-x py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className={b.products?.length ? "md:col-span-7" : "md:col-span-12 max-w-3xl"}>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight" data-testid={`home-banner-title-${b.id}`}>{b.title}</h2>
            {b.text && <p className="mt-4 text-bone/80 font-light leading-relaxed max-w-xl" data-testid={`home-banner-text-${b.id}`}>{b.text}</p>}
            {b.cta_label && b.cta_url && (
              <Link to={b.cta_url} className="inline-flex items-center gap-3 mt-7 border border-bone/50 px-6 py-3 min-h-[44px] text-xs tracking-[0.18em] uppercase hover:bg-bone hover:text-ink transition-colors" data-testid={`home-banner-cta-${b.id}`}>
                {b.cta_label} <ArrowUpRight size={14} />
              </Link>
            )}
          </div>
          {b.products?.length > 0 && (
            <div className="md:col-span-5 grid grid-cols-3 gap-3" data-testid={`home-banner-products-${b.id}`}>
              {b.products.slice(0, 3).map((p) => (
                <Link key={p.id} to="/shop" className="group block" data-testid={`home-banner-product-${p.id}`}>
                  <div className="aspect-square overflow-hidden bg-bone/10">
                    {p.image && <img src={resolveMedia(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />}
                  </div>
                  <div className="mt-2 text-[11px] leading-snug text-bone/80 truncate">{p.name}</div>
                  <div className="text-[11px] text-gold">{eur0(p.price)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const { settings } = useSettings();
  useSEO({
    title: "Architetto e Interior Designer a Napoli",
    description: "Progettazione d'interni, render, arredi su misura e direzione artistica a Napoli e online. Scopri i percorsi di Giovanna Matino.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Giovanna Matino Interior",
      description: "Studio di interior design: progettazione d'interni, render fotorealistici, direzione artistica della realizzazione.",
      telephone: settings.phone,
      email: settings.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: settings.address_line1,
        addressLocality: settings.address_line2,
        addressCountry: "IT",
      },
      url: window.location.origin,
      openingHours: "Mo-Sa 09:00-19:00",
      sameAs: [settings.instagram_url],
      areaServed: settings.service_areas,
      priceRange: "€€€",
    },
  });
  const [featured, setFeatured] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [services, setServices] = useState(staticServices);
  const [estMin, setEstMin] = useState(null);
  const [shopNews, setShopNews] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings.shop_news_enabled || !(settings.shop_news_ids || []).length) { setShopNews([]); return; }
    api.get("/products").then((r) => {
      const ids = settings.shop_news_ids;
      const byId = Object.fromEntries(r.data.map((p) => [p.id, p]));
      setShopNews(ids.map((id) => byId[id]).filter(Boolean));
    }).catch(() => {});
  }, [settings.shop_news_enabled, settings.shop_news_ids]);
  const [tIdx, setTIdx] = useState(0);
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  useEffect(() => {
    api.get("/portfolio", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
    api.get("/blog").then((r) => setBlogPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/services").then((r) => { if (r.data?.length) setServices(r.data); }).catch(() => {});
    api.get("/estimator/config").then((r) => {
      if (!r.data?.available) return;
      const prices = (r.data.packages || []).map((p) => p.price_from).filter((v) => v != null && isFinite(v));
      if (prices.length) setEstMin(Math.min(...prices));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTIdx((i) => (i + 1) % testimonials.length), 8000);
    return () => clearInterval(t);
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!marketingConsent) { toast.error("Serve il consenso marketing per l'iscrizione"); return; }
    setSubscribing(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, source: "home" });
      toast.success(data.already ? "Sei già iscritta!" : "Iscritta al Diario ✨");
      setEmail(""); setMarketingConsent(false);
    } catch { toast.error("Errore, riprova"); }
    finally { setSubscribing(false); }
  };

  return (
    <div data-testid={HOME.hero}>
      {/* HERO */}
      <section ref={heroRef} className="container-x pt-10 md:pt-20 pb-24 overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <motion.span
              className="eyebrow block"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
            >
              Architetto e Interior Designer · Napoli e Online
            </motion.span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-6">
              <MaskedLine delay={0.15}>Prima di ristrutturare,</MaskedLine>
              <MaskedLine delay={0.32}><em className="text-gold font-light">progetta ogni scelta.</em></MaskedLine>
            </h1>
            <motion.p
              className="mt-8 text-base md:text-lg text-taupe max-w-xl leading-relaxed font-light"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
            >
              Ti aiuto a definire spazi, stile, materiali e budget prima dell'inizio dei lavori,
              per evitare errori costosi, decisioni improvvisate e cantieri fuori controllo.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            >
              <motion.span whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="inline-flex">
                <Link to="/servizi" className="btn-ink" data-testid={HOME.ctaPrenota}>Scopri il percorso adatto a te</Link>
              </motion.span>
              <motion.span whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="inline-flex">
                <Link to="/portfolio" className="btn-outline-ink" data-testid={HOME.ctaPortfolio}>Guarda i progetti</Link>
              </motion.span>
            </motion.div>
            <motion.p
              className="mt-5 text-xs tracking-[0.14em] uppercase text-taupe"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              data-testid="home-hero-microcopy"
            >
              Consulenza iniziale gratuita di {settings.consultation_duration} · In studio oppure online
            </motion.p>
          </div>
          <div className="lg:col-span-6 relative">
            <motion.div
              className="absolute -top-6 -left-6 hidden md:block w-40 h-40 border border-gold/40"
              aria-hidden
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 1, ease: EASE }}
            />
            <motion.div
              className="relative overflow-hidden"
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
            >
              {(() => {
                const heroDesktop = settings.hero_image ? resolveMedia(settings.hero_image) : HERO_IMG;
                const heroMobile = settings.hero_image_mobile ? resolveMedia(settings.hero_image_mobile) : heroDesktop;
                const heroAlt = settings.hero_alt || "Interno elegante progettato da Giovanna Matino, architetto Napoli";
                const focal = HERO_FOCAL[settings.hero_focal] || "center center";
                const overlay = Math.min(70, Math.max(0, Number(settings.hero_overlay) || 0)) / 100;
                const resp = settings.hero_responsive || {};
                const srcSet = Object.entries(resp)
                  .map(([w, u]) => `${resolveMedia(u)} ${w}w`).join(", ");
                return (
                  <>
                    <motion.img
                      src={heroMobile}
                      alt={heroAlt}
                      className="md:hidden w-full h-[480px] object-cover scale-110 will-change-transform"
                      style={{ y: imgY, objectPosition: focal }}
                    />
                    <motion.img
                      src={heroDesktop}
                      srcSet={srcSet || undefined}
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      alt={heroAlt}
                      className="hidden md:block w-full h-[620px] object-cover scale-110 will-change-transform"
                      style={{ y: imgY, objectPosition: focal }}
                    />
                    {overlay > 0 && (
                      <div className="absolute inset-0 bg-ink pointer-events-none" style={{ opacity: overlay }} aria-hidden />
                    )}
                  </>
                );
              })()}
            </motion.div>
            <motion.div
              className="absolute -bottom-6 -right-6 hidden md:block w-40 h-40 bg-gold/10"
              aria-hidden
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 1.15, ease: EASE }}
            />
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee items={MARQUEE_ITEMS} />

      {/* BANNER PROMOZIONALI (gestiti da dashboard) */}
      {banners.length > 0 && (
        <section className="container-x py-10 md:py-14 space-y-6" data-testid="home-banners">
          {banners.map((b, i) => <BannerBlock key={b.id} b={b} index={i} />)}
        </section>
      )}

      {/* PROBLEMA */}
      <section className="border-b border-hair bg-white py-24" data-testid="home-section-problema">
        <div className="container-x">
          <Reveal>
            <div className="max-w-4xl">
              <Chapter n={1} label="Il Problema" />
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.1]">
                Una ristrutturazione senza un progetto completo <em className="text-gold font-light">costa molto più del progetto stesso.</em>
              </h2>
              <p className="mt-8 text-taupe leading-[1.9] font-light text-lg max-w-3xl">
                Distribuzioni cambiate durante i lavori. Prese dimenticate. Materiali che insieme non funzionano.
                Arredi fuori misura. Extra-costi che emergono quando ormai è troppo tardi.
              </p>
              <p className="mt-4 font-serif text-xl md:text-2xl text-ink italic max-w-3xl">
                Il mio lavoro è definire prima ciò che non deve essere improvvisato dopo.
              </p>
            </div>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              "Modifiche e ripensamenti in cantiere",
              "Budget che aumenta senza controllo",
              "Ambienti belli in foto ma poco funzionali",
              "Imprese e fornitori senza una regia unica",
            ].map((p, i) => (
              <Reveal key={p} delay={i * 0.1}>
                <div className="border border-hair p-6 bg-bone h-full hover:border-gold/60 transition-colors duration-500">
                  <AlertTriangle size={18} className="text-gold" strokeWidth={1.6} />
                  <p className="mt-4 font-serif text-lg leading-snug">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link to="/servizi" className="btn-ink inline-flex" data-testid="home-cta-evita-errori">Evita gli errori prima del cantiere</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RISULTATO */}
      <section className="container-x py-24" data-testid="home-section-risultato">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <Chapter n={2} label="Il Risultato" />
              <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
                Non ti consegno soltanto delle belle immagini. <em className="text-gold font-light">Ti consegno una direzione precisa.</em>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <ul className="space-y-4">
              {[
                "Spazi studiati sulle tue abitudini",
                "Stile e palette coerenti in tutta la casa",
                "Materiali e forniture definiti",
                "Decisioni prese prima dell'apertura del cantiere",
                "Disegni chiari per imprese e artigiani",
                "Maggiore controllo su tempi, costi e risultato finale",
              ].map((r, i) => (
                <Reveal key={r} delay={i * 0.07} y={20}>
                  <li className="flex gap-4 items-start text-ink border-b border-hair pb-4">
                    <Check size={18} className="text-gold mt-1 shrink-0" strokeWidth={2} />
                    <span className="text-lg font-light">{r}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="border-y border-hair bg-white py-24" data-testid="home-section-servizi">
        <div className="container-x">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
              <div>
                <Chapter n={3} label="I Percorsi" />
                <h2 className="font-serif text-4xl md:text-5xl mt-3">Tre modi di lavorare insieme.</h2>
              </div>
              <Link to="/servizi" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">Tutti i servizi <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, idx) => (
              <Reveal key={s.slug} delay={idx * 0.12} className={idx === 1 ? "md:-translate-y-4" : ""}>
                <Link to={`/servizi/${s.slug}`} className="group bg-bone border border-hair p-8 hover:border-gold transition-all duration-500 block h-full" data-testid={`home-service-${s.slug}`}>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-gold">{s.audience_label || s.tagline}</span>
                  <h3 className="font-serif text-3xl mt-3">{s.title}</h3>
                  <p className="mt-4 text-sm text-taupe leading-relaxed">{s.short_pitch || s.subtitle}</p>
                  <div className="mt-6 pt-5 border-t border-hair text-xs tracking-[0.14em] uppercase text-taupe">
                    {(s.highlights || []).slice(0, 2).join(" · ")}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">
                    {s.cta_short || "Scopri di più"} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP THE PROJECT — sezione editoriale */}
      {settings.shop_visible && (
        <section className="bg-ink text-bone py-24 overflow-hidden" data-testid="home-section-shop-the-project">
          <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6">
              <span className="eyebrow !text-gold">Shop the Project</span>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-5">Il progetto non finisce con un render.</h2>
              <p className="mt-6 text-bone/70 font-light leading-relaxed max-w-xl">
                Ricevi una Shopping List personalizzata con arredi, illuminazione e complementi selezionati
                dallo Studio, completa di caratteristiche, tempi indicativi, prezzi e collegamenti diretti all'acquisto.
              </p>
              <p className="mt-4 text-bone/70 font-light leading-relaxed max-w-xl">
                Per i clienti dei servizi aderenti: <span className="text-gold">condizioni esclusive sullo Shop</span>,
                riconosciute automaticamente — nessun coupon da cercare.
              </p>
              <Link to="/shop-the-project" className="mt-8 inline-flex items-center gap-2 border border-bone/50 px-6 py-3 min-h-[44px] text-xs tracking-[0.16em] uppercase hover:bg-bone hover:text-ink transition-colors" data-testid="home-stp-cta">
                Scopri Shop the Project <ArrowUpRight size={14} />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border border-gold/30" aria-hidden />
                <div className="relative overflow-hidden">
                  <FocalImage
                    settings={settings}
                    prefix="home_stp"
                    fallback="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600&q=85&auto=format"
                    alt="Ambiente progettato dallo Studio con arredi selezionati"
                    className="w-full h-[340px] md:h-[440px] object-cover"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* NOVITÀ SHOP */}
      {shopNews.length > 0 && (
        <section className="container-x py-20 md:py-24" data-testid="home-section-shop-news">
          <Reveal>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <span className="eyebrow text-gold">Shop</span>
                <h2 className="font-serif text-3xl md:text-5xl mt-4 leading-tight">{settings.shop_news_title || "Novità dallo Shop"}</h2>
                {settings.shop_news_intro && <p className="mt-4 text-taupe max-w-2xl font-light leading-relaxed">{settings.shop_news_intro}</p>}
              </div>
              <Link to="/shop" className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-5 py-3 min-h-[44px] hover:bg-ink hover:text-bone transition-colors" data-testid="shop-news-all">
                Tutto lo Shop <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
            {shopNews.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <Link to="/shop" className="group block" data-testid={`shop-news-card-${p.id}`}>
                  <div className="overflow-hidden bg-white border border-hair">
                    <img src={resolveMedia(p.image)} alt={p.name} className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="mt-4">
                    <div className="text-[10px] tracking-[0.14em] uppercase text-gold">{p.category}</div>
                    <h3 className="font-serif text-xl mt-1 group-hover:text-gold transition-colors">{p.name}</h3>
                    <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                      <span className="text-sm text-taupe">{eur0(p.price)}</span>
                      <span className={`text-[10px] tracking-[0.12em] uppercase ${p.availability === "non_disponibile" ? "text-red-700" : p.availability === "da_ordinare" ? "text-amber-700" : "text-emerald-700"}`}>
                        {p.availability === "da_ordinare" ? `Da ordinare${p.lead_time ? ` · ${p.lead_time}` : ""}` : p.availability === "non_disponibile" ? "Non disponibile" : "Disponibile"}
                      </span>
                    </div>
                    <span className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">Scopri <ArrowUpRight size={13} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* STIMA ISTANTANEA */}
      {estMin != null && (
        <section className="bg-ink text-bone py-16" data-testid="home-section-stima">
          <div className="container-x">
            <Reveal>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                <div className="max-w-2xl">
                  <span className="eyebrow text-gold">Calcola la tua stima gratuita</span>
                  <h2 className="font-serif text-3xl md:text-5xl mt-3 leading-tight text-bone">
                    Il tuo progetto, <em className="text-gold font-light">un prezzo chiaro</em> in 2 minuti.
                  </h2>
                  <p className="mt-4 text-bone/70 font-light leading-relaxed">
                    Poche domande, una stima preliminare personalizzata in PDF — elaborata sulle tariffe reali dello Studio, da {eur0(estMin)}. Gratuita e senza impegno.
                  </p>
                </div>
                <Link
                  to="/stima"
                  className="inline-flex items-center gap-3 px-8 py-4 min-h-[48px] text-sm tracking-[0.14em] uppercase bg-gold text-ink hover:bg-bone transition-colors shrink-0"
                  data-testid="home-cta-stima"
                >
                  Calcola la tua stima gratuita <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* QUALIFICATION */}
      <section className="container-x py-24" data-testid="home-section-qualification">
        <Reveal>
          <Chapter n={4} label="Chi ci sceglie" />
          <h2 className="font-serif text-4xl md:text-5xl mt-3 max-w-4xl leading-tight">
            Questo studio è adatto a te se…
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-10">
          <Reveal>
            <div>
              <div className="eyebrow text-gold mb-6">Sei nel posto giusto se</div>
              <ul className="space-y-4">
                {[
                  "Stai acquistando o ristrutturando un'abitazione",
                  "Vuoi prendere le decisioni prima di iniziare il cantiere",
                  "Cerchi un progetto personale, non una soluzione standard",
                  "Desideri un referente capace di dialogare con imprese e artigiani",
                  "Consideri il progetto un investimento e non una spesa superflua",
                ].map((r) => (
                  <li key={r} className="flex gap-3 border-b border-hair pb-4">
                    <Check size={16} className="text-gold mt-1 shrink-0" strokeWidth={2} />
                    <span className="text-ink font-light">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div>
              <div className="eyebrow text-taupe mb-6">Potremmo non essere la scelta giusta se</div>
              <ul className="space-y-4">
                {[
                  "Cerchi soltanto idee gratuite o immagini d'ispirazione",
                  "Vuoi iniziare i lavori senza aver definito il progetto",
                  "Scegli esclusivamente in base al prezzo più basso",
                  "Non sei disponibile a dedicare tempo alla fase iniziale di analisi",
                ].map((r) => (
                  <li key={r} className="flex gap-3 border-b border-hair pb-4">
                    <X size={16} className="text-taupe mt-1 shrink-0" strokeWidth={2} />
                    <span className="text-taupe font-light">{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="border-y border-hair bg-white py-24">
        <div className="container-x">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
              <div>
                <Chapter n={5} label="Progetti Selezionati" />
                <h2 className="font-serif text-4xl md:text-5xl mt-3">Lavori Recenti</h2>
              </div>
              <Link to="/portfolio" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">Tutti i progetti <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          {featured.length === 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[0,1,2].map((i) => <div key={i} className="w-full h-[420px] bg-hair/60 animate-pulse" />)}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6" data-testid={HOME.featuredGrid}>
              {featured.map((p, idx) => (
                <Reveal key={p.id} delay={idx * 0.12} className={idx === 1 ? "md:mt-16" : ""}>
                  <Link to="/portfolio" className="group block">
                    <div className="overflow-hidden">
                      <img src={resolveMedia(p.cover_image)} alt={`${p.title} — ${p.category} a ${p.location}`} className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="mt-5">
                      <div className="font-serif text-2xl">{p.title}</div>
                      <div className="text-xs tracking-[0.14em] uppercase text-taupe mt-1">{p.category} · {p.location}</div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* BLOG PREVIEW */}
      {blogPosts.length > 0 && (
        <section className="container-x py-24">
          <Reveal>
            <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
              <div>
                <Chapter n={6} label="Diario" />
                <h2 className="font-serif text-4xl md:text-5xl mt-3">Appunti di progettazione.</h2>
              </div>
              <Link to="/blog" className="hidden md:inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase link-underline">Tutti gli articoli <ArrowUpRight size={16} /></Link>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8" data-testid="home-blog-grid">
            {blogPosts.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.12} className={i === 1 ? "md:mt-16" : ""}>
                <Link to={`/blog/${p.slug}`} className="group block" data-testid={`home-blog-${p.slug}`}>
                  <div className="overflow-hidden">
                    <img src={resolveMedia(p.cover_image)} alt={p.title} className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="mt-5">
                    <span className="text-[10px] tracking-[0.16em] uppercase text-gold">{p.category || "Diario"}</span>
                    <h3 className="font-serif text-2xl mt-2 leading-tight group-hover:text-gold transition-colors">{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-white border-y border-hair py-24">
        <div className="container-x max-w-4xl">
          <Reveal>
            <span className="eyebrow">Testimonianze · Google</span>
            <blockquote key={tIdx} className="font-serif text-2xl md:text-3xl leading-[1.5] text-ink mt-6 animate-fade-up min-h-[240px] md:min-h-[180px]">
              "{testimonials[tIdx].text}"
            </blockquote>
            <div className="mt-6 flex items-center gap-4">
              <span className="hair-divider" />
              <div className="text-sm tracking-[0.14em] uppercase">{testimonials[tIdx].name}</div>
              <span className="text-gold text-sm">★★★★★</span>
            </div>
            <div className="mt-6 flex gap-2">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setTIdx(i)} className={`w-8 h-0.5 transition-colors ${i === tIdx ? "bg-gold" : "bg-ink/20 hover:bg-ink/40"}`} data-testid={`testimonial-dot-${i}`} aria-label={`Testimonianza ${i+1}`} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="container-x py-20">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-6">
              <span className="eyebrow">Diario · Newsletter</span>
              <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Iscriviti al Diario di Giovanna.</h2>
              <p className="mt-3 text-taupe font-light">Un'email al mese con appunti di progettazione, ispirazioni e anteprime dal cantiere. Cancellazione con un click.</p>
            </div>
            <form onSubmit={subscribe} className="md:col-span-6 space-y-3" data-testid="newsletter-form">
              <div className="flex gap-2">
                <Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="La tua email" className="rounded-none flex-1" data-testid="newsletter-email" />
                <button type="submit" disabled={subscribing || !marketingConsent} className="btn-ink disabled:opacity-40" data-testid="newsletter-submit">{subscribing ? "…" : "Iscriviti"}</button>
              </div>
              <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer">
                <input type="checkbox" checked={marketingConsent} onChange={(e)=>setMarketingConsent(e.target.checked)} className="mt-1" data-testid="newsletter-consent" />
                <span>Acconsento a ricevere comunicazioni commerciali. Consulta la <Link to="/privacy" className="underline hover:text-gold">Privacy Policy</Link>.</span>
              </label>
            </form>
          </div>
        </Reveal>
      </section>

      {/* CTA FINAL */}
      <section className="bg-ink text-bone py-24">
        <div className="container-x text-center">
          <Reveal>
            <span className="eyebrow text-gold">Iniziamo Insieme</span>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 max-w-3xl mx-auto leading-tight text-bone">
              Un progetto professionale <em className="text-gold font-light">costa meno degli errori che ti evita.</em>
            </h2>
            <Link to="/prenota" className="mt-10 inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.14em] uppercase border border-bone text-bone hover:bg-bone hover:text-ink transition-all duration-500" data-testid="home-cta-final">
              Prenota chiamata conoscitiva · Gratuita {settings.consultation_duration}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
